import {
    createLogger,
    createMetricsLogger,
    CTI,
    enableConsoleOnlyLogging,
    enableLocalHostLogging,
    IMetricsLoggerClient,
} from '@amzn/exs-client-logging/v2';
import {
    IElementPlugin,
    IElementsWebComponent,
    IElementsWebComponentOptions,
    PluginDisconnectHandler,
} from '@amzn/exs-elements-interface';
import { ILogFactory, ILogger } from '@amzn/exs-gem-interface';
import { Metric, onCLS, onFCP, onFID, onINP, onLCP, onTTFB } from 'web-vitals';
import { StandardMetricTypes } from '@amzn/exs-gem-metric-types';
import { emitCount, emitLatency } from '../../assets/scripts/metrics';
import { getCTIFromScriptUrl } from '../../assets/scripts/utils';

const {
    CumulativeLayoutShift,
    FirstContentfulPaint,
    FirstInputDelay,
    InteractionToNextPaint,
    LargestContentfulPaint,
    TimeToFirstByte,
} = StandardMetricTypes;

interface ClientSideMetricsPluginOptions {
    dimensions?: Array<Record<string, string>>;
    consoleOnlyLogging?: boolean;
    localHostLogging?: boolean;
}

/**
 * Register ClientSideMetrics plugin
 */
class Plugin implements IElementPlugin {
    logFactory: ILogFactory;

    logger: ILogger | undefined;

    metricLogger: IMetricsLoggerClient;

    dimensions: Array<Record<string, string>>;

    startTime: number;

    constructor(
        private cti: CTI,
        {
            consoleOnlyLogging,
            localHostLogging,
            dimensions,
        }: ClientSideMetricsPluginOptions = {}
    ) {
        if (consoleOnlyLogging) enableConsoleOnlyLogging();
        if (localHostLogging) enableLocalHostLogging();
        this.logFactory = createLogger(this.cti);
        this.metricLogger = createMetricsLogger(this.cti);
        this.dimensions = dimensions || [];
        this.startTime = Date.now();
    }

    configure<T extends IElementsWebComponentOptions>(options: T): T {
        this.logger = this.logFactory.create(options.tagName);
        return {
            ...options,
            logger: this.logger,
        };
    }

    onConnected(target: IElementsWebComponent): PluginDisconnectHandler {
        const getMetricLogger = () => {
            const metricLogger = this.metricLogger.new();
            metricLogger.setTimestamp(Date.now());
            if (this.dimensions.length > 0)
                metricLogger.setDimensions(...this.dimensions);

            return metricLogger;
        };

        const emit = (metricName: StandardMetricTypes) => (metric: Metric) =>
            emitLatency(getMetricLogger(), metricName, metric.value);

        onCLS(emit(CumulativeLayoutShift));
        onFCP(emit(FirstContentfulPaint));
        onFID(emit(FirstInputDelay));
        onINP(emit(InteractionToNextPaint));
        onLCP(emit(LargestContentfulPaint));
        onTTFB(emit(TimeToFirstByte));

        target.onRendered(async (event) => {
            const container = event.detail?.shadowRoot;
            if (container?.innerHTML?.length > 0) {
                emitCount(
                    getMetricLogger(),
                    StandardMetricTypes.RenderCount,
                    1
                );
                const latencyValue = Date.now() - this.startTime;
                emitLatency(
                    getMetricLogger(),
                    StandardMetricTypes.RenderLatency,
                    latencyValue
                );
            } else {
                emitCount(
                    getMetricLogger(),
                    StandardMetricTypes.RenderError,
                    1
                );
            }
        });
        return () => {};
    }
}

export const ClientSideMetricsPlugin = (
    cti: CTI,
    options?: ClientSideMetricsPluginOptions
): IElementPlugin => new Plugin(cti, options);

export function getClientSideMetricsPlugin(tagName: string) {
    return ClientSideMetricsPlugin(getCTIFromScriptUrl(import.meta.url), {
        dimensions: [
            {
                ElementName: tagName,
            },
        ],
        /* Set both of these options to true to have metrics logged into the browser console */
        consoleOnlyLogging: false,
        localHostLogging: false,
    });
}
